import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../ts/Glob';
import { IActionType } from '../../../../_common/platform/ts/printContentUtils';
import GlobalState from '../../../../_common/platform/ts/globalStates/GlobalState';
import * as Globalization from '../../../../_common/platform/ts/Globalization';
import * as reactGlobalClick from '../../../../_common/platform/ts/reactGlobalClick';
import { IWidgetProperties } from '../Widget';
import { WidgetData } from '../../../../_common/platform/ts/WidgetData';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import * as FilterUtilities from '../../../../_common/platform/tsx/filtering/FilterUtilities';
import { config } from '../../ts/config';
import * as permissions from '../../../../_common/platform/ts/permissions';
import { IWidget, IWidgetContextMenuItem } from '../../ts/Interfaces';
import * as appTheme from '../../../../_common/platform/ts/appTheme';
import { WidgetLinkParameters } from './../WidgetLinkParameters';
import { ICreateWidgetLinkOptions, WidgetLink_IncludeStates } from '../../ts/stores/WorkspaceStore';
import { IMenuItemFlags } from '../../../../_common/platform/ts/WidgetBase';

const MENU_ITEM_HEIGHT = 26;
const MENU_PADDING = 14;
const MIN_MENU_WIDTH = 160;
const DIVIDER_CLASS_NAME = 'dropdown-divider';

export function stringifyGlobalState(props: IWidgetProperties) {
    const globalState = GlobalState.getGlobalState(props.glob, props.tab);

    if (globalState != null) {
        const attributeFilters = globalState.getAttributeFiltersWithDefaultValues();
        const periodFilters = globalState.getPeriodFilters();

        const fullState = $.extend(props.namespaces, {
            'business-intelligence': {
                attributeFilters: attributeFilters,
                periodFilters: periodFilters
            }
        });

        return JSON.stringify({
            state: fullState,
            theme: props.theme
        });
    } else {
        return JSON.stringify({
            state: props.namespaces,
            theme: props.theme
        });
    }
}

export function finalizeMenuHandler(e) {
    reactGlobalClick.trigger(e);
    e.stopPropagation();
    e.preventDefault();
}

export function getOnCreateLinkHandler(props: IWidgetProperties, widgetData: WidgetData) {
    return (e) => {
        const { glob } = props;

        if (!props.isWorkspaceSaved) {
            glob.AppActions.showNotification_Fail('To use this feature, please save workspace first.');

            if (e != null) {
                e.stopPropagation();
                e.preventDefault();
            }
        } else {
            let infoStr: string;

            glob.DialogActions.createDialog(
                'Create Widget Link',
                null,
                (obj: ICreateWidgetLinkOptions) => {
                    const { data } = props;

                    let linkData = widgetData.widget.getWidgetLinkData(data.id, glob);

                    if (obj.includeStateAs === WidgetLink_IncludeStates.dbStateId) {
                        infoStr = stringifyGlobalState(props);
                    } else {
                        const globalState = GlobalState.getGlobalState(glob, props.tab);

                        if (globalState != null) {
                            // scopeOptions cannot be passed in here. So, it takes default or the first found Period Filter.
                            infoStr = globalState.getFilterAsURLParameters({});
                        }
                    }

                    if (obj.showLogo) {
                        linkData = _.extend(linkData || {}, {
                            showAppLogo: true
                        });
                    }

                    glob.AppActions.widgetCreateLink(
                        props.workspace,
                        data.id,
                        widgetData.values[data.type].name,
                        infoStr,
                        linkData,
                        obj
                    );
                },
                () => {},
                () => {},
                'custom',
                {
                    deny: false,
                    confirmTitle: 'OK',
                    resizable: false,

                    Component: WidgetLinkParameters,
                    componentOptions: {
                        glob: glob
                    }
                }
            );

            if (e != null) {
                finalizeMenuHandler(e);
            }
        }
    };
}

export function WidgetMenuItem(props: { caption: string; href?: string; onClick(e): void }) {
    return (
        <a className="dropdown-item" href={props.href || '#'} onClick={props.onClick}>
            {props.caption}
        </a>
    );
}

export function DividerMenuItem() {
    return <div className={DIVIDER_CLASS_NAME} />;
}

function ResetLayoutMenuItem(props: IWidgetProperties) {
    const handleOnClick = (e) => {
        props.glob.AppActions.widgetResetLayout(props.data.id);
        props.glob.AppActions.workspaceUpdateInitiate(true, props.workspace, props.tab, props.data.id);
        finalizeMenuHandler(e);
    };

    return <WidgetMenuItem caption={'Reset Layout'} onClick={handleOnClick} />;
}

export function getOnConfigClickHandler(
    workspace: number,
    tab: number,
    data: IWidget,
    theme: IValuesCollection,
    glob: IGlob,
    componentRefGetter: () => any
) {
    return (e) => {
        let readOnly = false;

        if (data.type === 'pdc-widget-drill-down') {
            const componentRef = componentRefGetter();

            if (
                componentRef &&
                componentRef.state &&
                componentRef.state.widgetState != null &&
                componentRef.state.widgetState.readOnlyWidgetEditor != null
            ) {
                readOnly = componentRef.state.widgetState.readOnlyWidgetEditor;
            }
        }

        glob.AppActions.widgetEdit(workspace, data, theme, tab, readOnly);
        glob.AppActions.widgetEditShow();
        finalizeMenuHandler(e);
    };
}

function RefreshMenuItem(props: IWidgetProperties) {
    const handleOnClick = (e) => {
        props.glob.AppActions.workspaceUpdateInitiate(false, props.workspace, props.tab, props.data.id);
        finalizeMenuHandler(e);
    };

    return <WidgetMenuItem caption={'Refresh'} onClick={handleOnClick} />;
}

function ResetAllMenuItem(props: { workspace: number; tab: number; theme: IValuesCollection; glob: IGlob }) {
    const handleOnClick = (e) => {
        props.glob.AppActions.resetFilters(props.workspace, props.tab, '_default');
        finalizeMenuHandler(e);
    };

    return <WidgetMenuItem caption={'Reset all Filters'} onClick={handleOnClick} />;
}

function ApplyMenuItem(props: { tab: number; theme: IValuesCollection; glob: IGlob }) {
    const handleOnClick = (e) => {
        const globalState = GlobalState.getGlobalState(props.glob, props.tab);

        globalState.applyShelvedFilters(props.theme);
        finalizeMenuHandler(e);
    };

    return <WidgetMenuItem caption={'Apply'} onClick={handleOnClick} />;
}

function MaximizeMenuItem(props: {
    workspace: number;
    tab: number;
    dataID: number;
    isWorkspaceSaved: boolean;
    theme: IValuesCollection;
    glob: IGlob;
}) {
    const handleOnClick = (e) => {
        finalizeMenuHandler(e);

        if (!props.isWorkspaceSaved) {
            props.glob.AppActions.showNotification_Fail('To use this feature, please save workspace first.');
        } else {
            props.glob.AppActions.workspaceViewShow(props.workspace);
            props.glob.WorkspaceActions.workspaceViewOpen(
                props.workspace,
                null,
                props.dataID,
                null,
                null,
                props.tab,
                props.theme
            );
        }
    };

    return (
        <WidgetMenuItem
            caption={'Maximize'}
            href={'#workspaces/' + props.workspace + '/widgets/' + props.dataID}
            onClick={handleOnClick}
        />
    );
}

// function FullScreenMenuItem(props: { _key: number; workspace: number; tab: number; dataID: number }) {
//     const getOnFullScreenHandler = (e) => {
//         finalizeMenuHandler(e);

//         // element which needs to enter full-screen mode
//         var element: any = document.querySelector('#widget-' + props._key + '-' + props.workspace + '-' + props.tab);
//         // make the element go to full-screen mode
//         let openFn =
//             element.requestFullscreen ||
//             element.mozRequestFullScreen ||
//             element.webkitRequestFullscreen ||
//             element.msRequestFullscreen;
//         if (openFn) {
//             element;
//             openFn
//                 .call(element)
//                 .then(function () {
//                     // element has entered fullscreen mode successfully
//                 })
//                 .catch(function (error) {
//                     // element could not enter fullscreen mode
//                 });
//         }
//         e.stopPropagation();
//         e.preventDefault();
//     };

//     return (
//         <WidgetMenuItem
//             caption={'Full Screen'}
//             href={'#workspaces/' + props.workspace + '/widgets/' + props.dataID}
//             onClick={getOnFullScreenHandler}
//         />
//     );
// }

export function getMenuItemFlags(constr, distractFree: boolean) {
    if (constr != null) {
        const flags: IMenuItemFlags = constr.getMenuItemFlags();

        if (distractFree) {
            flags.maximize = false;
            flags.createWidgetLink = false;
        }

        return flags;
    } else {
        return {};
    }
}

export function getPlatformMenuItems(
    widgetProperties: IWidgetProperties,
    widgetData: WidgetData,
    flags: IMenuItemFlags,
    theme: IValuesCollection,
    locale: string,
    componentRefGetter: () => any,
    onPrintHandlerGetter: (actionType: IActionType, theme: IValuesCollection) => (e) => void,
    getOnExportToExcelHandler: (
        theme: IValuesCollection,
        widgetProperties: IWidgetProperties,
        componentRefGetter
    ) => (e) => void
) {
    const result: React.JSX.Element[] = [];
    const { tab, glob } = widgetProperties;
    const themeFull = appTheme.getThemeObj(theme);

    let { maximize, createWidgetLink } = flags;

    if (config.LAYOUT_TYPE !== 'normal') {
        maximize = createWidgetLink = false;
    }

    if (maximize !== false && permissions.showMaximize() && glob.SettingsStore.useTabsMode()) {
        result.push(
            <MaximizeMenuItem
                key={'maximize'}
                workspace={widgetProperties.workspace}
                tab={tab}
                dataID={widgetProperties.data.id}
                isWorkspaceSaved={widgetProperties.isWorkspaceSaved}
                theme={theme}
                glob={glob}
            />
        );
    }

    if (createWidgetLink !== false && permissions.showCreateWidgetLink()) {
        result.push(
            <WidgetMenuItem
                key={'create-link'}
                caption={'Create Widget Link'}
                onClick={getOnCreateLinkHandler(widgetProperties, widgetData)}
            />
        );
    }

    if (flags.print !== false && themeFull['widget-show-print-button-in-menu'] === true) {
        result.push(
            <WidgetMenuItem
                key={'print-button'}
                caption={Globalization.getMessage('print', locale)}
                onClick={onPrintHandlerGetter(IActionType.print, theme)}
            />
        );
    }

    if (flags.print !== false && themeFull['widget-show-pdf-in-menu'] === true) {
        result.push(
            <WidgetMenuItem
                key={'generate-pdf'}
                caption={Globalization.getMessage('generatePDF', locale)}
                onClick={onPrintHandlerGetter(IActionType.exportToPdf, theme)}
            />
        );
    }

    if (flags.exportToExcel !== false && themeFull['widget-show-export-to-excel-in-menu'] === true) {
        result.push(
            <WidgetMenuItem
                key={'export-to-excel'}
                caption={Globalization.getMessage('exportToExcel', locale)}
                onClick={getOnExportToExcelHandler(themeFull, widgetProperties, componentRefGetter)}
            />
        );
    }

    if (result.length > 0 && (maximize !== false || createWidgetLink !== false)) {
        result.push(<DividerMenuItem key={result.length} />);
    }

    const useApply = flags.applyFilters && FilterUtilities.useApplyButton(theme);

    if (flags.refresh !== false) {
        result.push(<RefreshMenuItem key={'refresh'} {...widgetProperties} />);
    }

    if (useApply !== false) {
        result.push(<ApplyMenuItem key={'apply'} tab={tab} theme={theme} glob={glob} />);
    }

    if (flags.resetAllFilters) {
        result.push(
            <ResetAllMenuItem
                key={'reset-all'}
                workspace={widgetProperties.workspace}
                tab={tab}
                theme={theme}
                glob={glob}
            />
        );
    }

    if (result.length > 0 && (flags.refresh || useApply || flags.resetAllFilters)) {
        result.push(<DividerMenuItem key={result.length} />);
    }

    if (
        permissions.canEdit() &&
        (!widgetProperties.distractFree || widgetProperties.showEdit) &&
        !widgetProperties.hideEdit
    ) {
        result.push(
            <WidgetMenuItem
                key={'config'}
                caption={'Config'}
                onClick={getOnConfigClickHandler(
                    widgetProperties.workspace,
                    tab,
                    widgetProperties.data,
                    theme,
                    glob,
                    componentRefGetter
                )}
            />
        );
    } else {
        result.pop();
    }

    if (flags.resetLayout !== false) {
        result.push(<ResetLayoutMenuItem key={'reset-layout'} {...widgetProperties} />);
    }

    return result;
}

function getWidgetMenuItems(menuItemList: IWidgetContextMenuItem[], getMenuHandler: (callback) => (e) => void) {
    return menuItemList?.map((item, i) => {
        if (item.subMenu != null && item.subMenu.length > 0) {
            return (
                <div className="dropleft" key={'custom-menu-item-' + i}>
                    <a className="dropdown-item dropdown-toggle dropleft" href="#">
                        <span>{item.title}</span>
                        <div
                            className="dropdown-menu dropdown-menu-left"
                            style={{
                                height: item.subMenu.length * MENU_ITEM_HEIGHT + MENU_PADDING
                            }}
                        >
                            {getWidgetMenuItems(item.subMenu, getMenuHandler)}
                        </div>
                    </a>
                </div>
            );
        }

        if (item.divider) {
            return <DividerMenuItem key={'custom-menu-divider-' + i} />;
        }

        return (
            <div
                key={'custom-menu-item-' + i}
                className={'dropdown-item' + (item.active ? ' active' : '')}
                style={{ cursor: 'pointer' }}
                onClick={getMenuHandler(item.callback)}
            >
                {item.title}
            </div>
        );
    });
}

export function getOnShowWidgetHelpHandler(
    description: string,
    tooltip: string,
    glob: IGlob,
    showArrow: boolean = true
) {
    return (e) => {
        finalizeMenuHandler(e);
        glob.helpTools.showHelpDocument(glob.DialogActions, tooltip, null, description, e, showArrow);
    };
}

export function getMenuItems(
    constr,
    widgetData: WidgetData,
    theme: appTheme.IValuesCollection,
    platformMenuItems: React.JSX.Element[],
    description: string,
    tooltip: string,
    distractFree: boolean,
    glob: IGlob,
    getMenuHandler: (callback) => (e) => void
) {
    const menuItemList = constr != null ? constr.prototype.getMenuItems(widgetData.id, distractFree, theme) : [];

    let result = getWidgetMenuItems(menuItemList, getMenuHandler);

    if (platformMenuItems.length > 0) {
        if (result.length > 0) {
            result.unshift(<DividerMenuItem key="divider-widget-menu" />);
        }

        result = [...platformMenuItems].concat(result);
    }

    if (description != null && description !== '') {
        if (result.length > 0) {
            result.push(<DividerMenuItem key="divider-help" />);
        }

        result.push(
            <WidgetMenuItem
                key="help"
                caption={'Help'}
                onClick={getOnShowWidgetHelpHandler(description, tooltip, glob, false)}
            />
        );
    }

    return result;
}

function getMenuHeight(menuItems: React.JSX.Element[]) {
    let itemsCount = 0;
    let dividersCount = 0;

    menuItems.forEach((item) => {
        if (item.props.className === DIVIDER_CLASS_NAME) {
            dividersCount++;
        } else {
            itemsCount++;
        }
    });

    return MENU_PADDING + itemsCount * MENU_ITEM_HEIGHT + dividersCount * 17;
}

interface IWidgetMenuPopupOptions {
    menuItems: React.JSX.Element[];
    offset: JQuery.Coordinates;
    component?: any;
}

function WidgetMenuPopup(props: {
    zIndex: number;
    options: IWidgetMenuPopupOptions;
    glob: IGlob;

    close(): void;
}) {
    const globalClickIndex = React.useRef(0);
    const popoverBody = React.useRef<HTMLDivElement>(null);
    const [size, setSize] = React.useState<{
        height: number;
        width: number;
    }>({ width: MIN_MENU_WIDTH, height: getMenuHeight(props.options.menuItems) });

    React.useEffect(() => {
        const dialog = popoverBody.current;

        globalClickIndex.current = reactGlobalClick.register(props.close);

        setSize({
            height: dialog.clientHeight,
            width: dialog.clientWidth < MIN_MENU_WIDTH ? MIN_MENU_WIDTH : dialog.clientWidth
        });

        return () => {
            reactGlobalClick.unregister(globalClickIndex.current);
            globalClickIndex.current = undefined;
        };
    }, []);

    const { offset } = props.options;

    let menuItems = props.options.menuItems;
    let top = offset.top + 18;

    if (menuItems.length === 0) {
        menuItems = [
            <a key="empty">
                <em>{'Menu for this widget is empty'}</em>
            </a>
        ];
    } else {
        const windowHeight = $(window).height();

        if (top < 0) {
            top = 0;
        } else if (windowHeight - offset.top < size.height) {
            top = offset.top - size.height - 14;
        }
    }

    return (
        <div className="widget-menu">
            <div
                className={'dropdown-menu dropup'}
                style={{
                    display: 'block',
                    top: top,
                    left: offset.left - size.width + 24,
                    zIndex: props.zIndex
                }}
                ref={popoverBody}
            >
                {menuItems}
            </div>

            <div className="modal-backdrop show" onClick={finalizeMenuHandler} />
        </div>
    );
}

export function showWidgetMenu(menuItems: React.JSX.Element[], offset: JQuery.Coordinates, glob: IGlob) {
    glob.DialogActions.createPopup<IWidgetMenuPopupOptions>('custom', {
        menuItems: menuItems,
        offset: offset,
        component: WidgetMenuPopup
    });
}

export function WidgetMenuButton(props: { style: React.CSSProperties; title?: string; onClick: (e) => void }) {
    const [show, setShow] = React.useState(false);
    const globalClickIndex = React.useRef(null);

    let style = props.style;

    if (show) {
        style = {
            ...props.style,
            display: 'block',
            opacity: 0.8
        };
    }

    return (
        <div
            className="show-header-button d-print-none"
            style={style}
            title={props.title}
            onClick={(e) => {
                //EVG: Theese tricks are to make the button visible while the menu is shown.
                setShow(true);
                props.onClick(e);

                globalClickIndex.current = reactGlobalClick.register(() => {
                    reactGlobalClick.unregister(globalClickIndex.current);
                    globalClickIndex.current = undefined;

                    setShow(false);
                });
            }}
        >
            <span className="glyphicon glyphicon-option-horizontal"></span>
        </div>
    );
}
